import { SimpleChip } from '@/global/components/Chip'
import { hasPermission } from '@/hooks/useBackendMenu'
import { cn } from '@/lib/utils'
import { ocorrenciaTipoToPermission } from '@/service/api'
import { CategoriaEnum } from '@acme/auth'
import { StatusAprovacao } from '@acme/core'
import Box, { BoxProps } from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import DialogContent, { DialogContentProps } from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import LinearProgress from '@mui/material/LinearProgress'
import TextField from '@mui/material/TextField'
import { ChipButton } from 'global/components/ChipButton'
import { FormField } from 'global/components/Form'
import { useAuth } from 'hooks'
import React, { Fragment, useEffect, useState } from 'react'
import {
    Control,
    UseFormRegister,
    UseFormWatch,
    useFormContext,
    useFormState,
} from 'react-hook-form'
import { P, match } from 'ts-pattern'
import { z } from 'zod'
import DialogGuide from '.'
import { RootContext, RootContextProps } from './DialogGuideRoot'

interface ContentProps extends DialogContentProps {
    children?: React.ReactNode
    BoxProps?: BoxProps
}

export type { ContentProps as DialogContentProps }

function Content({ children, dividers, BoxProps, className }: ContentProps) {
    return (
        <DialogContent
            id={dividers ? 'scroll-dialog-description' : undefined}
            tabIndex={-1}
            dividers={dividers}
            sx={{
                scrollbarGutter: 'stable',
            }}
            className={className}
        >
            <RootContext.Consumer>
                {({ width = 'initial', height = 'initial' }) => (
                    <Box
                        {...BoxProps}
                        width={{
                            sm: !BoxProps?.width ? width : 'initial',
                            xs: '100%',
                        }}
                        height={{
                            sm: !BoxProps?.height ? height : 'initial',
                            xs: '100%',
                        }}
                        sx={{
                            ...BoxProps?.sx,
                            px: {
                                sm: 4,
                                xs: 1,
                            },
                            pt: dividers ? 2 : 1,
                            ...(BoxProps?.gap !== undefined && {
                                gap: BoxProps.gap,
                            }),
                        }}
                        className={cn(
                            'flex flex-col pb-1 max-w-full',
                            BoxProps?.className,
                        )}
                    >
                        {children}
                    </Box>
                )}
            </RootContext.Consumer>
        </DialogContent>
    )
}

interface ContentFooterProps {
    children?: React.ReactNode | ((ctx: RootContextProps) => React.ReactNode)
    onApprove?: (data: { id: number }) => void
    onReject?: (data: { id: number; motivo: string }) => void
    tipoOcorrencia?: Parameters<typeof ocorrenciaTipoToPermission>[0]
}

const ContentFooter: React.FC<ContentFooterProps> = ({
    children,
    onApprove,
    onReject,
    tipoOcorrencia,
}) => {
    const {
        decoded: { categoria },
        funcionarioSelecionado: user,
    } = useAuth()
    const formContext = useFormContext()
    const status = formContext?.watch('statusAprovacao')

    const [excluir, setExcluir] = useState(false)

    const handleClickExcluir =
        (action?: () => void) => (_: any, checked: boolean) => {
            action?.()
            setExcluir(checked)
        }

    useEffect(() => {
        if (excluir) {
            const element = document.getElementById('motivo_exclusao')

            if (element) {
                element.scrollIntoView({ behavior: 'smooth' })
                element.focus()
            }
        }
    }, [excluir])

    const exibirComponenteExclusao = match({
        categoria,
        status,
        ajuste: user?.ajustesPonto,
    })
        .with(
            {
                categoria: P.union(
                    CategoriaEnum.Cliente,
                    CategoriaEnum.Supervisor,
                    CategoriaEnum.Avaliacao,
                ),
                status: StatusAprovacao.APROVADO,
            },
            () => true,
        )
        .with({ categoria: CategoriaEnum.Funcionario, ajuste: 'S' }, () => true)
        .otherwise(() => false)

    return (
        <RootContext.Consumer>
            {ctx => (
                <Fragment>
                    {ctx.loading ? (
                        <Box width="100%" my={3}>
                            <LinearProgress />
                        </Box>
                    ) : (
                        <Divider sx={{ my: 3 }} />
                    )}
                    <Box display="flex" mb={2} alignItems="center">
                        {exibirComponenteExclusao && ctx.onDelete && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={ctx.delete}
                                        onChange={handleClickExcluir(
                                            ctx.toggleDelete,
                                        )}
                                    />
                                }
                                label={
                                    categoria != CategoriaEnum.Funcionario ||
                                    (categoria == CategoriaEnum.Funcionario &&
                                        status != StatusAprovacao.APROVADO)
                                        ? 'Deseja excluir este item?'
                                        : 'Solicitar exclusão'
                                }
                            />
                        )}
                        {tipoOcorrencia && (
                            <StatusButtons
                                watch={ctx.methods.watch}
                                onApprove={onApprove}
                                onReject={onReject}
                                loading={ctx.loading}
                                categoria={categoria}
                                tipoOcorrencia={tipoOcorrencia}
                            />
                        )}
                    </Box>
                    {status === StatusAprovacao.APROVADO ? (
                        <MotivoExclusao
                            register={ctx.methods.register}
                            control={ctx.methods.control}
                            excluir={excluir}
                        />
                    ) : null}
                    {typeof children === 'function' ? children(ctx) : children}
                </Fragment>
            )}
        </RootContext.Consumer>
    )
}

Content.Footer = ContentFooter

interface StatusButtonsProps {
    watch: UseFormWatch<any>
    onApprove?: any
    onReject?: any
    loading?: boolean
    categoria: number
    tipoOcorrencia: Parameters<typeof ocorrenciaTipoToPermission>[0]
}

function StatusButtons({
    watch,
    onApprove,
    onReject,
    loading,
    categoria,
    tipoOcorrencia,
}: StatusButtonsProps) {
    const [open, setOpen] = useState(false)
    const statusAprovacao = watch('statusAprovacao')
    const id = watch('id')
    const { PENDENTE, PENDENTE_EXCLUSAO } = StatusAprovacao

    if (
        statusAprovacao === PENDENTE_EXCLUSAO &&
        !hasPermission(['delete', ocorrenciaTipoToPermission(tipoOcorrencia)])
    ) {
        return null
    }

    return (
        <Box display="flex" flexGrow={1} justifyContent="flex-end" gap={1}>
            <SimpleChip status={statusAprovacao} />
            {[PENDENTE, PENDENTE_EXCLUSAO].includes(statusAprovacao) &&
                [
                    CategoriaEnum.Cliente,
                    CategoriaEnum.Supervisor,
                    CategoriaEnum.Avaliacao,
                ].includes(categoria) && (
                    <Fragment>
                        <ChipButton
                            tipo="aprovar"
                            onClick={() => {
                                if (onApprove) {
                                    onApprove({ id })
                                } else {
                                    throw new Error('onApprove não informado')
                                }
                            }}
                            title={
                                statusAprovacao === PENDENTE
                                    ? 'Aprovar registro'
                                    : 'Confirmar exclusão'
                            }
                            isLoading={loading}
                        />
                        <ChipButton
                            tipo="reprovar"
                            onClick={() => setOpen(true)}
                            title={
                                statusAprovacao === PENDENTE
                                    ? 'Reprovar registro'
                                    : 'Rejeitar exclusão'
                            }
                            isLoading={loading}
                        />
                        <DialogGuide.Root
                            open={open}
                            onClose={() => setOpen(false)}
                            height="6rem"
                            width="32rem"
                            title="Reprovar registro"
                            onSubmit={data => {
                                if (onReject) {
                                    onReject(data)
                                } else {
                                    throw new Error('onReject não informado')
                                }
                            }}
                            defaultValues={{
                                id,
                            }}
                            schema={
                                onReject
                                    ? z.object({
                                          motivo: z
                                              .string()
                                              .trim()
                                              .min(1, 'Obrigatório'),
                                      })
                                    : undefined
                            }
                        >
                            <DialogGuide.Title>
                                Reprovar registro
                            </DialogGuide.Title>
                            <DialogGuide.Content>
                                <FormField
                                    autoFocus
                                    name="motivo"
                                    label="Motivo"
                                    rows={2}
                                    multiline
                                    required
                                />
                                <FormField name="id" hidden />
                            </DialogGuide.Content>
                            <DialogGuide.Actions>
                                <DialogGuide.Actions.SecondaryButton>
                                    Cancelar
                                </DialogGuide.Actions.SecondaryButton>
                                <DialogGuide.Actions.PrimaryButton color="error">
                                    Reprovar
                                </DialogGuide.Actions.PrimaryButton>
                            </DialogGuide.Actions>
                        </DialogGuide.Root>
                    </Fragment>
                )}
        </Box>
    )
}

interface MotivoExclusaoProps {
    register: UseFormRegister<any>
    control: Control<any>
    excluir: boolean
}

function MotivoExclusao({ excluir, control, register }: MotivoExclusaoProps) {
    const { errors } = useFormState<{ motivo: string }>({
        control,
        name: 'motivo',
    })

    return (
        <TextField
            {...register('motivo')}
            disabled={false}
            variant="filled"
            id="motivo_exclusao"
            label="Motivo"
            fullWidth
            autoFocus
            multiline
            rows={2}
            sx={{
                display: excluir ? 'block' : 'none',
            }}
            helperText={
                errors.motivo?.message ||
                'Informe o motivo da exclusão deste registro'
            }
            error={!!errors.motivo?.message}
        />
    )
}

export { Content }
